// Formats card — the company's display defaults for language, dates, times and
// the week start. Saves through its own endpoint
// (POST companies/company-settings-format), independently of the details card.
//
// The Language here is the COMPANY default. It shares the AppLanguage type with
// the signed-in user's `appLanguage` and with languageSlice's current UI locale,
// but never the value: switching it must not flip the console out from under
// whoever is editing.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import SettingsCard, { SettingsRow } from './SettingsCard';
import {
  DATE_FORMATS,
  LANGUAGES,
  TIME_FORMATS,
  TIME_ZONES,
  WEEK_DAYS,
  labelOf,
  toSelectOptions,
} from './companySettingsOptions';
import { Field, SelectDropdown } from '@/components/common';
import {
  useUpdateCompanyFormatsMutation,
  type FormatSettings,
  type WeekDay,
} from '@/api/endpoints/companySettingsApi';
import type { ApiErrorShape } from '@/api/types';
import type { AppLanguage } from '@/redux/language/languageSlice';

interface FormatsCardProps {
  formats: FormatSettings;
}

const FormatsCard = ({ formats }: FormatsCardProps) => {
  const { t } = useTranslation();
  const [updateFormats, { isLoading: saving }] = useUpdateCompanyFormatsMutation();

  const seed = useMemo<FormatSettings>(() => ({ ...formats }), [formats]);
  const [draft, setDraft] = useState<FormatSettings>(seed);
  const [editing, setEditing] = useState(false);
  useEffect(() => setDraft(seed), [seed]);

  const patch = (changes: Partial<FormatSettings>) => setDraft((d) => ({ ...d, ...changes }));

  const dirty =
    draft.mainLanguage !== seed.mainLanguage ||
    draft.dateFormat !== seed.dateFormat ||
    draft.timeFormat !== seed.timeFormat ||
    draft.timeZone !== seed.timeZone ||
    draft.startOfWeek !== seed.startOfWeek;

  const cancel = () => {
    setDraft(seed);
    setEditing(false);
  };

  const save = async () => {
    try {
      await updateFormats(draft).unwrap();
      // Closed here, not in the re-seed effect, so a second edit already in
      // flight when the refetch lands isn't stomped.
      setEditing(false);
      toast.success(t('company_settings_formats_saved'));
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  return (
    <SettingsCard
      title={t('company_settings_formats_title')}
      subtitle={t('company_settings_formats_subtitle')}
      editing={editing}
      dirty={dirty}
      saving={saving}
      canSave={dirty}
      onEdit={() => setEditing(true)}
      onCancel={cancel}
      onSave={() => void save()}
    >
      {editing ? (
        <div className='company-settings__form'>
          <Field label={t('company_settings_language')}>
            <SelectDropdown<AppLanguage>
              size='lg'
              options={toSelectOptions(LANGUAGES, t)}
              value={draft.mainLanguage}
              onChange={(mainLanguage) => patch({ mainLanguage })}
            />
          </Field>
          <Field label={t('company_settings_date_format')}>
            <SelectDropdown
              size='lg'
              options={toSelectOptions(DATE_FORMATS, t)}
              value={draft.dateFormat}
              onChange={(dateFormat) => patch({ dateFormat })}
            />
          </Field>
          <Field label={t('company_settings_time_format')}>
            <SelectDropdown
              size='lg'
              options={toSelectOptions(TIME_FORMATS, t)}
              value={draft.timeFormat}
              onChange={(timeFormat) => patch({ timeFormat })}
            />
          </Field>
          <Field label={t('company_settings_time_zone')}>
            <SelectDropdown
              size='lg'
              options={toSelectOptions(TIME_ZONES, t)}
              value={draft.timeZone}
              onChange={(timeZone) => patch({ timeZone })}
            />
          </Field>
          <Field label={t('company_settings_start_of_week')}>
            <SelectDropdown<WeekDay>
              size='lg'
              options={toSelectOptions(WEEK_DAYS, t)}
              value={draft.startOfWeek}
              onChange={(startOfWeek) => patch({ startOfWeek })}
            />
          </Field>
        </div>
      ) : (
        <div className='company-settings__rows'>
          <SettingsRow
            label={t('company_settings_language')}
            value={labelOf(LANGUAGES, seed.mainLanguage, t)}
          />
          <SettingsRow
            label={t('company_settings_date_format')}
            value={labelOf(DATE_FORMATS, seed.dateFormat, t)}
            ltr
          />
          <SettingsRow
            label={t('company_settings_time_format')}
            value={labelOf(TIME_FORMATS, seed.timeFormat, t)}
          />
          <SettingsRow
            label={t('company_settings_time_zone')}
            value={labelOf(TIME_ZONES, seed.timeZone, t)}
            ltr
          />
          <SettingsRow
            label={t('company_settings_start_of_week')}
            value={labelOf(WEEK_DAYS, seed.startOfWeek, t)}
          />
        </div>
      )}
    </SettingsCard>
  );
};

export default FormatsCard;
